import React, { useState, useEffect } from 'react';
import { Typography, Tooltip } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import VenueSelectionStep from './components/Steps/VenueSelectionStep/VenueSelectionStep';
import UnionLoginPinStep from './components/Steps/UnionLoginPinStep/UnionLoginPinStep';
import ConfigurePhotoStep from './components/Steps/ConfigurePhotoStep/ConfigurePhotoStep';
import { selectVenue } from '../../../redux/venues/venuesSlice';
import { logout } from '../../../redux/actions/userActions';
import { setOnboardingComplete } from '../../../redux/slices/currentUserSlice';
import { BackButtonContainer, HeaderContainer, TitleContainer, CombinedContainer } from './Onboarding.styles';
import AuthContainer from './components/AuthContainer/AuthContainer';

const steps = [
  { label: 'Select restaurant', component: VenueSelectionStep },
  { label: 'Connect account', component: UnionLoginPinStep },
  { label: 'Profile Photo', component: ConfigurePhotoStep },
];

const Onboarding = () => {
  const currentUser = useSelector((state) => state.currentUser);
  const profileImageUrl = currentUser?.employeeInfo?.profileImageUrl;

  const [activeStep, setActiveStep] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Animations for steps
  const stepVariants = {
    initial: { opacity: 1 }, // Starts invisible
    animate: { opacity: 1 }, // Appears completely visible
    exit: { opacity: 0 }, // Fade out
  };

  const isFirstStep = activeStep === 0;

  // Skip this step if we have a profile image already!
  useEffect(() => {
    if (profileImageUrl && activeStep === 2) {
      dispatch(setOnboardingComplete());
      navigate('/loading');
    }
  }, [profileImageUrl, activeStep, dispatch, navigate]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleBack = () => {
    if (isFirstStep) {
      handleLogout();
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleVenueSelect = (venueId) => {
    dispatch(selectVenue(venueId));
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleSuccess = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const getStepContent = (stepIndex) => {
    const StepComponent = steps[stepIndex]?.component;

    if (!StepComponent) {
      return <Typography variant="h6">Unknown step</Typography>;
    }

    const stepProps = {};
    if (stepIndex === 0) stepProps.onVenueSelect = handleVenueSelect;
    if (stepIndex === 1) stepProps.onSuccess = handleSuccess;

    return <StepComponent {...stepProps} />;
  };

  return (
    <AuthContainer>
      <CombinedContainer>
        <HeaderContainer>
          <Tooltip placement="top-start" title={isFirstStep ? 'You will be logged out' : ''} arrow>
            <BackButtonContainer onClick={handleBack}>
              <ArrowBackIosIcon sx={{ padding: '0', fontSize: '23px' }} />
              <Typography variant="bodyRegular">Back</Typography>
            </BackButtonContainer>
          </Tooltip>
          {steps[activeStep]?.label && (
            <TitleContainer>
              <Typography variant="bodyEmphasized" align="center">
                {steps[activeStep].label}
              </Typography>
            </TitleContainer>
          )}
        </HeaderContainer>

        {/* Step content transition */}
        <AnimatePresence mode="wait">
          {/* Ensures only one step is mounted at a time */}
          <motion.div
            key={activeStep} // Change animation when change step
            variants={stepVariants} // Define the animation variants
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.5 }} // Animation duration
            style={{ width: '100%' }} // Ensures the container has the correct width
          >
            {getStepContent(activeStep)}
          </motion.div>
        </AnimatePresence>
      </CombinedContainer>
    </AuthContainer>
  );
};

export default Onboarding;
